import { MAX_DATA_LAYER_NAME_LENGTH } from './data-layer-name.js';
export { MAX_DATA_LAYER_NAME_LENGTH } from './data-layer-name.js';
import { dataLayerStyleSchema } from './data-layer-style.js';
export { dataLayerStyleSchema } from './data-layer-style.js';
import { kmlSourceSchema, kmlArchiveSchema, type KmlSource } from './kml-source.js';
import { urlLayerSourceSchema, type UrlLayerSource } from './url-layer-source.js';
import { dataImageSchema, type DataImage } from './data-image.js';
import { validDataPolygonGeometry } from './polygon-validation.js';
import { type DataIcon } from './data-icon.js';
import { z } from 'zod';
import type { SchemaInput, SchemaRecord } from './schema-input.js';
import { jsonByteLength } from './json-size.js';
import { isNumber } from './runtime-values.js';

export const DATA_LAYER_PAYLOAD_VERSION = 1 as const;
export const MAX_DATA_LAYER_FEATURES = 50_000;
export const MAX_DATA_LAYER_PAYLOAD_BYTES = 25 * 1024 * 1024;
export const PRO_DATA_LAYER_FEATURES = 100_000;
export const PRO_DATA_LAYER_PAYLOAD_BYTES = 32 * 1024 * 1024;
export const PRO_DATA_LAYER_LIMITS = { maxFeatures: PRO_DATA_LAYER_FEATURES, maxBytes: PRO_DATA_LAYER_PAYLOAD_BYTES };


export type DataLayerSourceFormat = 'geojson' | 'csv' | 'tsv' | 'xlsx' | 'kml' | 'kmz' | 'gpx' | 'shapefile';
export type SupportedGeoJsonGeometryType = 'Point' | 'MultiPoint' | 'LineString' | 'MultiLineString' | 'Polygon' | 'MultiPolygon';
type GeometryCoordinates = {
  Point: number[];
  MultiPoint: number[][];
  LineString: number[][];
  MultiLineString: number[][][];
  Polygon: number[][][];
  MultiPolygon: number[][][][];
};
export type DataGeometry = ({ [Kind in SupportedGeoJsonGeometryType]: { type: Kind; coordinates: GeometryCoordinates[Kind] } }[SupportedGeoJsonGeometryType]
  | { type: 'GeometryCollection'; geometries: DataGeometry[]; coordinates?: never }) & { bbox?: number[]; [member: string]: SchemaInput };
export interface DataLayerStyle { pointRadius?: number; pointOutlineColor?: string; routeArrows?: boolean; routeEditable?: boolean; routeMarkerKm?: 0 | 1 | 5 | 10; routeElevation?: boolean; icon?: DataIcon; color: string; width?: number; opacity?: number; fillColor?: string; fillOpacity?: number }
export interface DataFeature { kmlSource?: KmlSource; importLayerId?: string; visible?: boolean; image?: DataImage; id: string; sourceKey?: string | number; coordinateFields?: { latitude: string; longitude: string }; geometry: DataGeometry | null; properties: SchemaRecord; nullProperties?: boolean; bbox?: number[]; sourceStyle?: Partial<DataLayerStyle>; provenance?: DataLayerProvenance }
export const importSourceFileSchema = z.object({ kml: kmlArchiveSchema.optional(), name: z.string().min(1).max(255).regex(/^[^\\/]+\.(gpx|kml|kmz|geojson|json|csv|tsv|xlsx|zip)$/i), base64: z.string().max(20 * 1024 * 1024).regex(/^[A-Za-z0-9+/]*={0,2}$/).refine((value) => value.length % 4 === 0) }).strict();
export const importSourceFilesSchema = z.array(importSourceFileSchema).max(32);
export type ImportSourceFile = z.infer<typeof importSourceFileSchema>;

const provenanceUrlSchema = z.string().min(1).max(2048).url().refine((value) => /^https?:\/\//i.test(value), 'Source links must use HTTP or HTTPS.');
export const originalMetadataSchema = z.object({
  filename: z.string().min(1).max(255), sourceUrl: provenanceUrlSchema,
  sha256: z.string().regex(/^[a-f0-9]{64}$/), encoding: z.literal('base64'),
  content: z.string().max(2_000_000).regex(/^[A-Za-z0-9+/]*={0,2}$/),
}).strict();

export const dataLayerProvenanceSchema = z.object({
  catalogSlug: z.string().min(1).max(120).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  assetId: z.string().min(1).max(120),
  assetVersion: z.string().min(1).max(120),
  sourceName: z.string().min(1).max(300),
  sourceUrl: provenanceUrlSchema,
  license: z.string().min(1).max(300),
  licenseUrl: provenanceUrlSchema,
  attribution: z.string().min(1).max(4000),
  period: z.string().min(1).max(300).optional(),
  originalMetadata: z.array(originalMetadataSchema).max(16).optional(),
  prepared: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  notes: z.array(z.string().min(1).max(2000)).max(20),
}).strict();
export type DataLayerProvenance = z.infer<typeof dataLayerProvenanceSchema>;

export const dataLayerDisplaySchema = z.object({
  group: z.array(z.string().trim().min(1).max(80)).max(6).optional(),
  locked: z.boolean().optional(),
  filters: z.array(z.object({ field: z.string().min(1).max(300), operator: z.enum(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'contains']), value: z.string().max(4000) }).strict()).max(8).optional(),
  nameFields: z.array(z.string().min(1).max(120)).min(1).max(20).optional(),
  showUnnamed: z.boolean().optional(),
  showLabels: z.boolean().optional(),
  fieldFilter: z.object({ field: z.string().min(1).max(300), value: z.string().max(4000) }).strict().optional(),
  colorBy: z.object({ field: z.string().min(1).max(300), numeric: z.boolean().optional(), categories: z.array(z.object({
    value: z.string().max(4000), upperBound: z.number().finite().optional(), label: z.string().max(4000), color: z.string().regex(/^#[0-9a-f]{6}$/i),
  }).strict()).min(1).max(12) }).strict().optional(),
}).strict();
export type DataLayerDisplay = z.infer<typeof dataLayerDisplaySchema>;

export interface DataLayerMetadata {
  id: string; name: string; sourceFormat: DataLayerSourceFormat; style: DataLayerStyle; visible: boolean;
  position: number; featureCount: number; bounds: [number, number, number, number] | null; saved: boolean;
  urlSource?: UrlLayerSource;
  display?: DataLayerDisplay;
  persisted?: boolean; draftId?: string; bbox?: number[]; sourceFiles?: ImportSourceFile[]; provenance?: DataLayerProvenance;
}
export interface DatasetReference { id: string; count: number; columns: string[] }
export interface RetainedSourceFile { name: string; data: Blob }
export interface DataLayer extends DataLayerMetadata { serverVersion?: number; features: DataFeature[]; dataset?: DatasetReference; retainedSources?: RetainedSourceFile[] }
export interface DataLayerPayload { version: typeof DATA_LAYER_PAYLOAD_VERSION; layer: Omit<DataLayerMetadata, 'saved' | 'persisted' | 'draftId'>; features: DataFeature[] }
export type DataLayerValidationResult =
  | { success: true; payload: DataLayerPayload; errors: string[]; code?: never }
  | { success: false; payload?: never; errors: string[]; code?: 'payload_limit' };

const positionSchema = z.array(z.number()).min(2);
const pointListSchema = z.array(positionSchema);
const lineStringSchema = pointListSchema.min(2);
const ringSchema = pointListSchema.min(4).refine((ring) => {
  const last = ring.at(-1);
  return last !== undefined && ring[0].length === last.length && ring[0].every((value, index) => value === last[index]);
}, 'Polygon rings must have matching first and last positions.');
const geometryBoundsSchema = z.array(z.number()).min(4).refine((bounds) => bounds.length % 2 === 0);
export const dataGeometrySchema: z.ZodType<DataGeometry> = z.lazy(() => z.discriminatedUnion('type', [
  z.object({ type: z.literal('Point'), coordinates: positionSchema, bbox: geometryBoundsSchema.optional() }).catchall(z.json()),
  z.object({ type: z.literal('MultiPoint'), coordinates: pointListSchema.min(1), bbox: geometryBoundsSchema.optional() }).catchall(z.json()),
  z.object({ type: z.literal('LineString'), coordinates: lineStringSchema, bbox: geometryBoundsSchema.optional() }).catchall(z.json()),
  z.object({ type: z.literal('MultiLineString'), coordinates: z.array(lineStringSchema).min(1), bbox: geometryBoundsSchema.optional() }).catchall(z.json()),
  z.object({ type: z.literal('Polygon'), coordinates: z.array(ringSchema).min(1), bbox: geometryBoundsSchema.optional() }).catchall(z.json()),
  z.object({ type: z.literal('MultiPolygon'), coordinates: z.array(z.array(ringSchema).min(1)).min(1), bbox: geometryBoundsSchema.optional() }).catchall(z.json()),
  z.object({ type: z.literal('GeometryCollection'), geometries: z.array(dataGeometrySchema), coordinates: z.never().optional(), bbox: geometryBoundsSchema.optional() }).catchall(z.json()),
]));
export const editedDataGeometrySchema = dataGeometrySchema.refine(validDataPolygonGeometry, 'Polygon rings must have nonzero area and no crossing edges.');
export const coordinateFieldsSchema = z.object({ latitude: z.string().min(1), longitude: z.string().min(1) }).strict();
export const dataFeatureSchema = z.object({ kmlSource: kmlSourceSchema.optional(), importLayerId: z.string().max(100).optional(), visible: z.boolean().optional(), image: dataImageSchema.optional(), id: z.string().min(1).max(100), sourceKey: z.union([z.string().max(500), z.number()]).optional(), geometry: dataGeometrySchema.nullable(), coordinateFields: coordinateFieldsSchema.optional(), nullProperties: z.boolean().optional(), bbox: z.array(z.number()).optional(), properties: z.record(z.string(), z.json()), sourceStyle: dataLayerStyleSchema.partial().optional(), provenance: dataLayerProvenanceSchema.optional() }).strict();
const boundsSchema = z.tuple([z.number(), z.number(), z.number(), z.number()]).nullable();
export const dataLayerPayloadSchema = z.object({
  version: z.literal(DATA_LAYER_PAYLOAD_VERSION),
  layer: z.object({ id: z.string().min(1).max(100), name: z.string().min(1).max(MAX_DATA_LAYER_NAME_LENGTH), sourceFormat: z.enum(['geojson', 'csv', 'tsv', 'xlsx', 'kml', 'kmz', 'gpx', 'shapefile']), style: dataLayerStyleSchema, visible: z.boolean(), position: z.number().int().nonnegative(), featureCount: z.number().int().nonnegative(), bounds: boundsSchema, bbox: z.array(z.number()).optional(), sourceFiles: importSourceFilesSchema.optional(), urlSource: urlLayerSourceSchema.optional(), provenance: dataLayerProvenanceSchema.optional(), display: dataLayerDisplaySchema.optional() }).strict(),
  features: z.array(dataFeatureSchema).max(PRO_DATA_LAYER_FEATURES),
}).strict();

const payloadEnvelopeSchema = dataLayerPayloadSchema.extend({ features: z.array(z.unknown()) });

function isPosition(value: SchemaInput): value is number[] {
  if (!Array.isArray(value) || value.length < 2) return false;
  for (const coordinate of value) {
    if (!isNumber(coordinate) || !Number.isFinite(coordinate)) return false;
  }
  return true;
}

export function calculateDataLayerBounds(features: readonly DataFeature[]): [number, number, number, number] | null {
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  const geometries = features.flatMap((feature) => feature.geometry ? [feature.geometry] : []);
  const values: SchemaInput[] = [];
  while (geometries.length) {
    const geometry = geometries.pop()!;
    if (geometry.type === 'GeometryCollection') {
      for (const child of geometry.geometries) geometries.push(child);
    }
    else values.push(geometry.coordinates);
  }
  while (values.length) {
    const value = values.pop();
    if (isPosition(value)) {
      minX = Math.min(minX, value[0]); minY = Math.min(minY, value[1]);
      maxX = Math.max(maxX, value[0]); maxY = Math.max(maxY, value[1]);
    } else if (Array.isArray(value)) {
      for (const child of value) values.push(child);
    }
  }
  return Number.isFinite(minX) ? [minX, minY, maxX, maxY] : null;
}

export function validateDataLayerPayload(value: SchemaInput, options: { reuseFeatures?: boolean; maxFeatures?: number; maxBytes?: number } = {}): DataLayerValidationResult {
  const maxBytes = options.maxBytes ?? MAX_DATA_LAYER_PAYLOAD_BYTES;
  if (jsonByteLength(value, maxBytes) > maxBytes) return { success: false, code: 'payload_limit', errors: ['The normalized data layer is too large.'] };
  const parsed = payloadEnvelopeSchema.safeParse(value);
  if (!parsed.success) return { success: false, errors: parsed.error.issues.map((issue) => issue.path.includes('geometry') ? 'A feature has unsupported or invalid geometry.' : issue.message) };
  if (parsed.data.features.length > (options.maxFeatures ?? MAX_DATA_LAYER_FEATURES)) return { success: false, code: 'payload_limit', errors: ['The data layer has too many features.'] };
  const errors: string[] = [];
  const ids = new Set<string>();
  const features: DataFeature[] = [];
  parsed.data.features.forEach((candidate, index) => {
    const checked = dataFeatureSchema.safeParse(candidate);
    if (!checked.success) { errors.push(...checked.error.issues.map((issue) => issue.path.includes('geometry') ? 'A feature has unsupported or invalid geometry.' : issue.message)); return; }
    // SAFETY: The strict feature schema above validates every field without transforms.
    // Keep the validated input; retaining the schema's clone doubles large layers.
    const feature = options.reuseFeatures ? candidate as DataFeature : checked.data;
    features.push(feature);
    if (ids.has(feature.id)) errors.push(`Feature ${index + 1} has a duplicate ID.`);
    ids.add(feature.id);
    const image = feature.image;
    if (image?.kind === 'screen' && feature.geometry !== null) errors.push('Screen images must not have map geometry.');
    if (image?.kind === 'photo' && feature.geometry?.type !== 'Point') errors.push('Photo images must have point geometry.');
    if (image?.kind === 'ground' && (feature.geometry?.type !== 'Polygon' || JSON.stringify(feature.geometry.coordinates) !== JSON.stringify([[...image.corners, image.corners[0]]]))) errors.push('Ground image geometry must match its corners.');
  });
  if (parsed.data.layer.featureCount !== parsed.data.features.length) errors.push('Layer feature count does not match the payload.');
  const payload: DataLayerPayload = { ...parsed.data, features };
  if (JSON.stringify(payload.layer.bounds) !== JSON.stringify(calculateDataLayerBounds(payload.features))) errors.push('Layer bounds do not match the payload.');
  return errors.length ? { success: false, errors } : { success: true, payload, errors };
}

export function dataLayerPayload(layer: DataLayer): DataLayerPayload {
  const { features, saved: _saved, persisted: _persisted, draftId: _draftId, serverVersion: _serverVersion, retainedSources: _retainedSources, dataset: _dataset, ...metadata } = layer;
  return { version: DATA_LAYER_PAYLOAD_VERSION, layer: metadata, features };
}

export function randomDataId(prefix: 'layer' | 'feature' | 'draft'): string {
  const value = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${value}`;
}
